import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Phone, ArrowRight, Play, X, CheckCircle2, ShieldCheck, BadgeCheck, Award,
  Factory, PencilRuler, Sparkles, MapPin,
} from "lucide-react";
import {
  COMPANY, ASSETS, HERO, TRUST_BADGES, SERVICES, WHY_US, STEPS, GALLERY,
  TESTIMONIALS, STATS, FAQS,
} from "../mock";
import {
  SectionHeading, ServiceCard, ProcessSteps, StatsBar, TestimonialCard, FAQAccordion, StarRow,
} from "../components/common";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";

const ICONS = { Factory, PencilRuler, Sparkles, ShieldCheck };

export default function Home() {
  const navigate = useNavigate();
  const [videoOpen, setVideoOpen] = useState(false);
  useScrollReveal();
  useSEO({
    title: "Screen & Pool Enclosures in Melbourne, FL | Miller & Miller Services LLC",
    description: "Family-owned screen & pool enclosure specialists in Melbourne, FL. Custom pool cages, patio & porch screens, rescreening, carports & lanai enclosures. Free estimates - call 321-608-0255.",
    keywords: ["screen enclosure company Melbourne FL", "pool screen enclosures", "pool cage installation", "screen enclosures", "lanai enclosure", "patio screening", "Brevard County"],
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "GeneralContractor",
      name: "Miller & Miller Services LLC",
      image: "https://millermillerservices.com/photos/stock-1.jpg",
      telephone: "+13216080255",
      email: "info@millermillerservices.com",
      url: "https://millermillerservices.com",
      areaServed: ["Melbourne FL", "West Melbourne FL", "Palm Bay FL", "Viera FL", "Rockledge FL", "Cocoa FL", "Merritt Island FL", "Satellite Beach FL", "Indialantic FL", "Melbourne Beach FL", "Titusville FL"],
      address: { "@type": "PostalAddress", addressLocality: "Melbourne", addressRegion: "FL", addressCountry: "US" },
      priceRange: "$$",
      aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "100" },
    },
  });

  return (
    <div>
      {/* ============ HERO with video background ============ */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay muted loop playsInline poster={ASSETS.pool1}
        >
          <source src={ASSETS.heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/90 via-brand-navy/75 to-brand-ocean/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navydark/90 via-transparent to-transparent" />

        <div className="container-x relative z-10 pt-24 pb-12 md:pt-28 md:pb-16">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-[0.18em] text-white bg-white/10 ring-1 ring-white/25 rounded-full px-4 py-2 animate-fade-in">
              <MapPin className="h-3.5 w-3.5 text-brand-gold2" /> {HERO.eyebrow}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-heading font-extrabold text-white leading-[1.05]">
              Creating Luxurious <span className="text-gradient-sky">Retreats</span> In Your Backyard
            </h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">{HERO.subtitle}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => navigate("/estimate")}
                className="h-14 px-8 text-base bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold"
              >
                Get Your Free Estimate <ArrowRight className="h-5 w-5 ml-1" />
              </Button>
              <button
                onClick={() => setVideoOpen(true)}
                className="h-14 px-6 rounded-md flex items-center gap-3 text-white font-heading font-bold bg-white/10 hover:bg-white/20 ring-1 ring-white/25 transition-colors"
              >
                <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-gold text-brand-navy">
                  <Play className="h-4 w-4 fill-brand-navy ml-0.5" />
                </span>
                Watch Our Work
              </button>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
              {TRUST_BADGES.slice(0, 3).map((b) => (
                <div key={b} className="flex items-center gap-2 text-white/90 text-sm font-semibold">
                  <CheckCircle2 className="h-5 w-5 text-brand-sky2" /> {b}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* floating phone card */}
        <a
          href={COMPANY.phoneHref}
          className="hidden md:flex absolute bottom-8 right-8 z-10 items-center gap-3 bg-white rounded-2xl shadow-2xl px-5 py-4 hover:-translate-y-1 transition-transform"
        >
          <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-ocean text-white">
            <Phone className="h-6 w-6" />
          </span>
          <span>
            <span className="block text-xs font-semibold text-brand-slate uppercase tracking-wider">Call today</span>
            <span className="block text-xl font-heading font-extrabold text-brand-navy">{COMPANY.phone}</span>
          </span>
        </a>
      </section>

      {/* ============ Trust marquee ============ */}
      <div className="bg-brand-navy overflow-hidden py-4 border-y border-white/10">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...TRUST_BADGES, ...TRUST_BADGES, ...TRUST_BADGES, ...TRUST_BADGES].map((b, i) => (
            <span key={i} className="flex items-center gap-2 mx-6 text-white/70 font-heading font-bold text-sm uppercase tracking-wide">
              <BadgeCheck className="h-4 w-4 text-brand-gold2" /> {b}
            </span>
          ))}
        </div>
      </div>

      {/* ============ About / Who we are ============ */}
      <section className="section-y bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal relative">
            <div className="rounded-3xl overflow-hidden shadow-card">
              <img src={ASSETS.pool2} alt="Custom screened pool enclosure in Melbourne, FL" className="w-full h-[440px] object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-6 -left-2 sm:left-6 bg-brand-gold rounded-2xl px-6 py-5 shadow-gold">
              <div className="text-3xl font-heading font-extrabold text-brand-navy">500+</div>
              <div className="text-sm font-semibold text-brand-navy/80">Backyards Transformed</div>
            </div>
            <div className="absolute -top-5 -right-2 sm:right-6 bg-white rounded-2xl px-5 py-4 shadow-card ring-1 ring-brand-mist flex items-center gap-3">
              <Award className="h-8 w-8 text-brand-ocean" />
              <div>
                <div className="font-heading font-extrabold text-brand-navy leading-none">Lifetime</div>
                <div className="text-xs text-brand-slate">Warranty</div>
              </div>
            </div>
          </div>

          <div>
            <SectionHeading
              eyebrow="Who We Are"
              title="The Premium Screen Specialist in Melbourne, FL"
              subtitle="Miller & Miller Services LLC is trusted by homeowners across the Space Coast. Every project is built with durability and precision - from your first consultation to the final walkthrough, we make it smooth, clean, and worth talking about."
            />
            <div className="mt-7 grid sm:grid-cols-2 gap-4 reveal">
              {TRUST_BADGES.map((b) => (
                <div key={b} className="flex items-center gap-3 rounded-xl bg-brand-mist/60 px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-ocean shrink-0" />
                  <span className="font-semibold text-brand-navy text-sm">{b}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3 reveal">
              <Button onClick={() => navigate("/services")} className="h-12 px-7 bg-brand-ocean hover:bg-brand-sky text-white font-heading font-bold">
                Explore Services
              </Button>
              <Link to="/gallery" className="inline-flex items-center gap-2 h-12 px-7 rounded-md ring-1 ring-brand-slate/25 hover:ring-brand-ocean text-brand-navy font-heading font-bold transition-all">
                View Our Work <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Services ============ */}
      <section className="section-y bg-brand-mist/40">
        <div className="container-x">
          <SectionHeading
            center
            eyebrow="Our Services"
            title="Premium Outdoor Solutions"
            subtitle="From elite pool enclosures to durable carports, we design and build custom exterior solutions engineered for Florida living."
          />
          <div className="mt-8 md:mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.id} service={s} index={i} />
            ))}
          </div>
          <div className="text-center mt-10 reveal">
            <Button onClick={() => navigate("/services")} className="h-12 px-8 bg-brand-navy hover:bg-brand-ocean text-white font-heading font-bold">
              See All Services <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* ============ Why choose us ============ */}
      <section className="section-y bg-white">
        <div className="container-x">
          <SectionHeading center eyebrow="Why Choose Us" title="Craftsmanship You Can Count On" />
          <div className="mt-8 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {WHY_US.map((w, i) => {
              const Icon = ICONS[w.icon] || ShieldCheck;
              return (
                <div key={i} className="reveal group rounded-2xl bg-white p-7 ring-1 ring-brand-mist hover:ring-brand-sky/40 hover:shadow-card transition-all" style={{ transitionDelay: `${i * 60}ms` }}>
                  <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-mist text-brand-ocean group-hover:bg-brand-ocean group-hover:text-white transition-colors">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-5 text-lg font-heading font-extrabold text-brand-navy">{w.title}</h3>
                  <p className="mt-2 text-sm text-brand-slate leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ Process + Stats ============ */}
      <section className="section-y bg-brand-navy relative overflow-hidden">
        <div className="absolute -top-32 -left-24 h-80 w-80 rounded-full bg-brand-ocean/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 h-80 w-80 rounded-full bg-brand-sky/20 blur-3xl" />
        <div className="container-x relative">
          <SectionHeading center light eyebrow="How It Works" title="From Consultation to Retreat" subtitle="A simple, transparent process designed to get you enjoying your new outdoor space fast." />
          <div className="mt-8 md:mt-12">
            <ProcessSteps steps={STEPS} />
          </div>
          <div className="mt-14">
            <StatsBar stats={STATS} />
          </div>
        </div>
      </section>

      {/* ============ Gallery preview ============ */}
      <section className="section-y bg-white">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <SectionHeading eyebrow="Featured Projects" title="See Our Work" subtitle="A glimpse of the backyard retreats we've built across the Space Coast." />
            <Link to="/gallery" className="reveal inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-ocean hover:bg-brand-sky text-white font-heading font-bold transition-colors shrink-0">
              View Full Gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {GALLERY.slice(0, 8).map((g, i) => (
              <Link
                key={i}
                to="/gallery"
                className={`reveal group relative overflow-hidden rounded-xl ${i === 0 || i === 5 ? "col-span-2 row-span-2" : ""}`}
                style={{ transitionDelay: `${(i % 4) * 60}ms` }}
              >
                <img src={g.src} alt={g.title} loading="lazy" className="h-full w-full object-cover aspect-square transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <span className="text-white font-heading font-bold text-sm">{g.title}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Testimonials ============ */}
      <section className="section-y bg-brand-mist/40">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <SectionHeading eyebrow="Customer Testimonials" title="What Our Homeowners Say" />
            <div className="reveal flex items-center gap-3">
              <StarRow n={5} />
              <span className="font-heading font-extrabold text-brand-navy">5.0 · Trusted by hundreds</span>
            </div>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} t={t} />)}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="section-y bg-white">
        <div className="container-x grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
          <div>
            <SectionHeading eyebrow="Got Questions?" title="Frequently Asked Questions" subtitle="Everything you need to know about working with Miller & Miller Services." />
            <div className="mt-7 reveal rounded-2xl bg-brand-navy p-7 text-white">
              <h3 className="font-heading font-extrabold text-xl">Still have questions?</h3>
              <p className="text-white/70 mt-2 text-sm">Our team is happy to help. Call us or request a free estimate today.</p>
              <a href={COMPANY.phoneHref} className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-bold transition-colors">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
          </div>
          <FAQAccordion faqs={FAQS} />
        </div>
      </section>

      {/* Video modal */}
      {videoOpen && (
        <div className="fixed inset-0 z-[1000] grid place-items-center bg-black/80 p-4" onClick={() => setVideoOpen(false)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setVideoOpen(false)} aria-label="Close video" className="absolute -top-12 right-0 h-10 w-10 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25">
              <X className="h-6 w-6" />
            </button>
            <video className="w-full rounded-xl shadow-2xl" controls autoPlay playsInline>
              <source src={ASSETS.heroVideo} type="video/mp4" />
            </video>
          </div>
        </div>
      )}
    </div>
  );
}
